// services/razorpayService.ts

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, any>;
  theme?: {
    color?: string;
  };
  modal?: {
    ondismiss?: () => void;
    confirm_close?: boolean;
    escape?: boolean;
    animation?: boolean;
  };
  retry?: {
    enabled: boolean;
  };
  on_payment_failed?: (response: any) => void;
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface PaymentVerificationData {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  user_payment_id: number;
  fk_user_id: number;
}

export interface UpdatePaymentStatusRequest {
  user_id: number;
  user_payment_id: number;
  order_id: string;
  payment_id: string;
  signature: string;
  payment_status: 'pending' | 'completed' | 'failed' | 'cancelled';
}

export interface UpdatePaymentStatusResponse {
  success: string;
  message: string;
  [key: string]: any;
}

class RazorpayService {
  private static instance: RazorpayService;
  private isScriptLoaded: boolean = false;
  private scriptLoadPromise: Promise<void> | null = null;
  private trivaLogoDataUrl: string | null = null;
  private razorpayKeyId: string;

  private constructor() {
    // Get Razorpay Key ID from environment variable
    this.razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
    
    // Validate that the key exists
    if (!this.razorpayKeyId) {
      console.error('❌ REACT_APP_RAZORPAY_KEY_ID is not defined in environment variables');
      throw new Error('Razorpay Key ID is required. Please set REACT_APP_RAZORPAY_KEY_ID in your .env file');
    }
    
    // Initialize logo data URL when service is created
    this.initializeTrivaLogo();
  }

  public static getInstance(): RazorpayService {
    if (!RazorpayService.instance) {
      RazorpayService.instance = new RazorpayService();
    }
    return RazorpayService.instance;
  }

  /**
   * Get the Razorpay Key ID from environment
   */
  private getRazorpayKey(): string {
    if (!this.razorpayKeyId) {
      throw new Error('Razorpay Key ID is not configured. Please check your .env file');
    }
    return this.razorpayKeyId;
  }

  /**
   * Initialize Triva logo using hosted image
   */
  private initializeTrivaLogo(): void {
    // Using the hosted image for payment modal
    this.trivaLogoDataUrl = 'https://play-lh.googleusercontent.com/Dy1wHRSQY2k1HxVoI3gtXkd_gsX_rGklPxyDznJEFtkPJE64vF4mYOIToyN7-QQ44KU=w240-h480-rw';
  }

  /**
   * Pre-load the Razorpay script. Call from useEffect on mount so it is
   * ready before the user taps Pay, enabling a fully-synchronous open path.
   */
  public preloadScript(): Promise<void> {
    return this.loadRazorpayScript();
  }

  /**
   * Returns true only when the script has finished loading and window.Razorpay exists.
   */
  public isScriptReady(): boolean {
    return this.isScriptLoaded && !!window.Razorpay;
  }

  /**
   * Open a Razorpay payment modal synchronously (no await).
   * Requires the script to be pre-loaded via preloadScript().
   * Calling this directly from a click handler (no await before it) keeps the
   * browser's user-gesture context intact, which is what iOS Chrome requires
   * to allow the Razorpay popup.
   */
  public openPaymentSync(options: RazorpayOptions): void {
    if (!window.Razorpay) {
      throw new Error('Razorpay script not loaded. Call preloadScript() first.');
    }

    let paymentFailed = false;
    let paymentSucceeded = false;

    const wrappedOptions: RazorpayOptions = {
      ...options,
      handler: (response: RazorpayResponse) => {
        paymentSucceeded = true;
        options.handler(response);
      },
      retry: { enabled: false },
      modal: {
        ...options.modal,
        ondismiss: () => {
          if (!paymentFailed && !paymentSucceeded && options.modal?.ondismiss) {
            options.modal.ondismiss();
          }
        },
      },
    };

    const rzp = new window.Razorpay(wrappedOptions);

    rzp.on('payment.failed', (response: any) => {
      paymentFailed = true;
      rzp.close();
      if (options.on_payment_failed) {
        options.on_payment_failed(response);
      }
    });

    rzp.open();
  }

  /**
   * Load Razorpay script dynamically
   */
  private loadRazorpayScript(): Promise<void> {
    if (this.isScriptLoaded) {
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.Razorpay) {
        this.isScriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      
      script.onload = () => {
        this.isScriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        this.scriptLoadPromise = null;
        reject(new Error('Failed to load Razorpay script'));
      };
      
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  /**
   * Initialize Razorpay payment
   */
  public async initiatePayment(options: RazorpayOptions): Promise<void> {
    try {
      await this.loadRazorpayScript();

      if (!window.Razorpay) {
        throw new Error('Razorpay SDK not loaded');
      }

      // Track payment outcome to suppress stray ondismiss callbacks.
      // Razorpay fires ondismiss after rzp.close() (called on payment.failed)
      // and sometimes after a successful handler call — guard both cases.
      let paymentFailed = false;
      let paymentSucceeded = false;

      const wrappedOptions: RazorpayOptions = {
        ...options,
        handler: (response: RazorpayResponse) => {
          paymentSucceeded = true;
          options.handler(response);
        },
        // Disable Razorpay's built-in retry UI so it never shows the retry screen
        retry: { enabled: false },
        modal: {
          ...options.modal,
          ondismiss: () => {
            console.log('🟠 RAZORPAY ondismiss fired — paymentFailed:', paymentFailed, 'paymentSucceeded:', paymentSucceeded);
            if (!paymentFailed && !paymentSucceeded && options.modal?.ondismiss) {
              options.modal.ondismiss();
            }
          }
        }
      };

      console.log('🟡 RAZORPAY: Opening modal with options:', {
        key: options.key,
        amount: options.amount,
        currency: options.currency,
        order_id: options.order_id,
        name: options.name,
        description: options.description,
        prefill: options.prefill,
      });

      const rzp = new window.Razorpay(wrappedOptions);

      rzp.on('payment.failed', (response: any) => {
        console.error('🔴 RAZORPAY payment.failed event fired:');
        console.error('  error.code:', response?.error?.code);
        console.error('  error.description:', response?.error?.description);
        console.error('  error.source:', response?.error?.source);
        console.error('  error.step:', response?.error?.step);
        console.error('  error.reason:', response?.error?.reason);
        console.error('  metadata.order_id:', response?.error?.metadata?.order_id);
        console.error('  metadata.payment_id:', response?.error?.metadata?.payment_id);
        console.error('  Full response:', JSON.stringify(response, null, 2));
        paymentFailed = true;
        rzp.close();
        if (options.on_payment_failed) {
          options.on_payment_failed(response);
        }
      });

      rzp.open();
      console.log('🟢 RAZORPAY: rzp.open() called successfully');
    } catch (error) {
      console.error('Error initiating Razorpay payment:', error);
      throw error;
    }
  }

  /**
   * Create payment options for Razorpay with logo-only display
   */
  public createPaymentOptions({
    orderId,
    amount,
    currency = 'INR',
    name = '',
    description,
    userDetails,
    onSuccess,
    onFailure,
    onDismiss,
    notes = {}
  }: {
    orderId: string;
    amount: number;
    currency?: string;
    name?: string;
    description: string;
    userDetails?: {
      name?: string;
      email?: string;
      contact?: string;
    };
    onSuccess: (response: RazorpayResponse) => void;
    onFailure?: (error: any) => void;
    onDismiss?: () => void;
    notes?: Record<string, any>;
  }): RazorpayOptions {
    return {
      key: this.getRazorpayKey(), // Get key from environment only
      amount: Math.round(parseFloat(amount.toFixed(2)) * 100), // Amount in paise (exact to nearest paisa)
      currency,
      name: name || ' ', // Use empty space if no name provided
      description,
      image: this.trivaLogoDataUrl || undefined,
      order_id: orderId,
      handler: (response: RazorpayResponse) => {
        try {
          onSuccess(response);
        } catch (error) {
          console.error('Error in success handler:', error);
          if (onFailure) {
            onFailure(error);
          }
        }
      },
      prefill: userDetails ? {
        name: userDetails.name || '',
        email: userDetails.email || '',
        contact: userDetails.contact || ''
      } : undefined,
      notes: {
        ...notes,
        created_at: new Date().toISOString(),
        platform: 'web',
        user_agent: navigator.userAgent
      },
      theme: {
        color: '#0DB5B5'
      },
      modal: {
        ondismiss: () => {
          if (onDismiss) {
            try {
              onDismiss();
            } catch (error) {
              console.error('Error in dismiss handler:', error);
            }
          }
        },
        confirm_close: true,
        escape: true,
        animation: true
      },
      on_payment_failed: onFailure ? (response: any) => {
        try {
          onFailure(response);
        } catch (error) {
          console.error('Error in failure handler:', error);
        }
      } : undefined
    };
  }

  /**
   * Set a custom logo for payments
   */
  public setCustomLogo(logoUrl: string): void {
    this.trivaLogoDataUrl = logoUrl;
  }

  /**
   * Get the current logo URL
   */
  public getCurrentLogo(): string | null {
    return this.trivaLogoDataUrl;
  }

  /**
   * Update payment status on your backend
   */
  public async updatePaymentStatus(
    updateData: UpdatePaymentStatusRequest,
    apiUrl: string
  ): Promise<UpdatePaymentStatusResponse> {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result: UpdatePaymentStatusResponse = await response.json();
      
      if (result.success !== "1") {
        throw new Error(result.message || 'Failed to update payment status');
      }

      return result;
    } catch (error) {
      console.error('Payment status update failed:', error);
      throw error;
    }
  }

  /**
   * Verify payment signature
   */
  public async verifyPayment(verificationData: PaymentVerificationData, apiUrl: string): Promise<any> {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verificationData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Payment verification failed:', error);
      throw error;
    }
  }

  /**
   * Handle payment success with automatic status update
   */
  public async handlePaymentSuccess(
    response: RazorpayResponse,
    userPaymentId: number,
    userId: number,
    orderId: string,
    updateStatusApiUrl: string,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) {
    try {
      const updateData: UpdatePaymentStatusRequest = {
        user_id: userId,
        user_payment_id: userPaymentId,
        order_id: orderId,
        payment_id: response.razorpay_payment_id,
        signature: response.razorpay_signature,
        payment_status: 'completed'
      };

      const result = await this.updatePaymentStatus(updateData, updateStatusApiUrl);
      
      if (onSuccess) {
        onSuccess({
          razorpayResponse: response,
          updateResult: result
        });
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Handle payment failure with automatic status update
   */
  public async handlePaymentFailure(
    error: any,
    userPaymentId: number | null,
    userId: number | null,
    orderId: string | null,
    updateStatusApiUrl: string,
    onError?: (error: any) => void
  ) {
    console.error('Payment failed:', error);
    
    if (userId && userPaymentId && orderId) {
      try {
        const updateData: UpdatePaymentStatusRequest = {
          user_id: userId,
          user_payment_id: userPaymentId,
          order_id: orderId,
          payment_id: '',
          signature: '',
          payment_status: 'failed'
        };

        await this.updatePaymentStatus(updateData, updateStatusApiUrl);
      } catch (updateError) {
        console.error('Failed to update payment status to failed:', updateError);
      }
    }
    
    if (onError) {
      onError(error);
    } else {
      alert('Payment failed. Please try again.');
    }
  }

  /**
   * Handle payment dismissal with automatic status update
   */
  public async handlePaymentDismiss(
    userPaymentId: number | null,
    userId: number | null,
    orderId: string | null,
    updateStatusApiUrl: string,
    onDismiss?: () => void
  ) {
    if (userId && userPaymentId && orderId) {
      try {
        const updateData: UpdatePaymentStatusRequest = {
          user_id: userId,
          user_payment_id: userPaymentId,
          order_id: orderId,
          payment_id: '',
          signature: '',
          payment_status: 'cancelled'
        };

        await this.updatePaymentStatus(updateData, updateStatusApiUrl);
      } catch (updateError) {
        console.error('Failed to update payment status to cancelled:', updateError);
      }
    }
    
    if (onDismiss) {
      onDismiss();
    }
  }

  /**
   * Create comprehensive payment handler
   */
  public createPaymentHandler({
    userId,
    orderId,
    amount,
    description,
    userDetails,
    notes = {},
    onSuccess,
    onFailure,
    onDismiss,
    updateStatusApiUrl,
    displayName = ''
  }: {
    userId: number;
    orderId: string;
    amount: number;
    description: string;
    userDetails?: {
      name?: string;
      email?: string;
      contact?: string;
    };
    notes?: Record<string, any>;
    onSuccess?: (data: any) => void;
    onFailure?: (error: any) => void;
    onDismiss?: () => void;
    updateStatusApiUrl: string;
    displayName?: string;
  }) {
    let userPaymentId: number | null = null;

    return {
      setUserPaymentId: (id: number) => {
        userPaymentId = id;
      },
      
      getPaymentOptions: (): RazorpayOptions => {
        return this.createPaymentOptions({
          orderId,
          amount,
          description,
          userDetails,
          notes,
          name: displayName,
          onSuccess: async (response: RazorpayResponse) => {
            if (userPaymentId) {
              await this.handlePaymentSuccess(
                response,
                userPaymentId,
                userId,
                orderId,
                updateStatusApiUrl,
                onSuccess,
                onFailure
              );
            }
          },
          onFailure: async (error: any) => {
            await this.handlePaymentFailure(
              error,
              userPaymentId,
              userId,
              orderId,
              updateStatusApiUrl,
              onFailure
            );
          },
          onDismiss: async () => {
            await this.handlePaymentDismiss(
              userPaymentId,
              userId,
              orderId,
              updateStatusApiUrl,
              onDismiss
            );
          }
        });
      }
    };
  }

  /**
   * Validate payment response
   */
  public validatePaymentResponse(response: RazorpayResponse): boolean {
    return !!(
      response &&
      response.razorpay_payment_id &&
      response.razorpay_order_id &&
      response.razorpay_signature
    );
  }

  /**
   * Format payment amount for display
   */
  public formatAmount(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  }
}

export default RazorpayService;